import axios from 'axios';
import fetchClient from 'utils/axiosConfig';
import constants, { isAdminUser } from 'utils/constants';

const isPublic = !isAdminUser;

export const getWebPages = async (eventId) => {
    if (!eventId) return;
    const url = constants.API.WEB_DESIGNER.PAGES.replace(':eventId', eventId);
    const queryParams = {
        params: {
            sortBy: 'createdAt|asc',
        },
        tokenCheck: false,
    };
    return fetchClient
        .get(isPublic ? `public/${url}` : url, queryParams)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const deleteWebPageDetailById = (eventId, pageId) => {
    const url = constants.API.WEB_DESIGNER.PAGES.replace(':eventId', eventId);
    return fetchClient.delete(`${url}/${pageId}`);
};

export const getWebPageDetailById = (eventId, pageId) => {
    const url = constants.API.WEB_DESIGNER.PAGES.replace(':eventId', eventId);
    return fetchClient.get(
        isPublic ? `public/${url}/${pageId}` : `${url}/${pageId}`,
        { tokenCheck: false }
    );
};

export const saveWebPageAction = (requestData, pageId, eventId) => {
    const url = constants.API.WEB_DESIGNER.PAGES.replace(':eventId', eventId);
    if (!pageId) {
        return fetchClient.post(url, requestData);
    }
    return fetchClient.patch(`${url}/${pageId}`, requestData);
};

export const deletePageBackgroundImage = (eventId, pageId) => {
    const url = constants.API.WEB_DESIGNER.PAGES.replace(':eventId', eventId);
    return fetchClient.delete(`${url}/${pageId}/background-image`);
};

export const getWebWidgets = async (
    eventId,
    pageId,
    isWebView,
    parentWidgetId
) => {
    if (!eventId || !pageId) return;
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient
        .get(
            `${isPublic ? `public/${url}` : url}${
                parentWidgetId ? `/${parentWidgetId}` : ''
            }`,
            { tokenCheck: false }
        )
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const getWebWidgetDetailById = (eventId, pageId, widgetId) => {
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient.get(
        isPublic ? `public/${url}/${widgetId}` : `${url}/${widgetId}`,
        { tokenCheck: false }
    );
};

export const saveWebWidgetAction = (requestData, eventId, pageId, widgetId) => {
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    if (!widgetId) {
        return fetchClient.post(url, requestData);
    }
    return fetchClient.patch(`${url}/${widgetId}`, requestData);
};

export const saveWebWidgetFileAction = (
    eventId,
    widgetId,
    pageId,
    requestData
) => {
    const config = {
        headers: { 'content-type': 'multipart/form-data' },
    };
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient
        .patch(`${url}/${widgetId}/file`, requestData, config)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const deleteWebWidgetFileAction = (eventId, widgetId, pageId) => {
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient
        .delete(`${url}/${widgetId}/file`)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const deleteWebWidgetById = (eventId, pageId, widgetId) => {
    const url = constants.API.WEB_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient.delete(`${url}/${widgetId}`);
};

export const reorderWebWidgetsAction = (requestData, eventId, pageId) => {
    const url = constants.API.WEB_DESIGNER.REORDER_WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    const data = {
        order: requestData,
    };
    return fetchClient.post(url, data);
};

let cancelToken;

export const getDirectoryForWeb = ({
    eventId,
    pageId,
    page,
    pageSize,
    filters,
}) => {
    // Check if there are any previous pending requests
    if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
    }
    // Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();

    const queryParams = {
        params: {
            page,
            pageSize,
            filters,
        },
        cancelToken: cancelToken.token,
        tokenCheck: false,
    };
    const url = constants.API.WEB_DESIGNER.WEB_DIRECTORY.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient.get(isPublic ? `public/${url}` : url, queryParams);
};

export const getDirectoryFiltersForWeb = (eventId, pageId) => {
    try {
        const url = constants.API.WEB_DESIGNER.WEB_DIRECTORY_FILTERS.replace(
            ':eventId',
            eventId
        ).replace(':pageId', pageId);
        return fetchClient
            .get(isPublic ? `public/${url}` : url, { tokenCheck: false })
            .then(({ data }) => ({ success: true, data }))
            .catch((error) => ({
                success: false,
                message: error?.response?.data?.message || '',
            }));
    } catch {
        return { success: false };
    }
};

export const getWebEventDetailById = (eventId) => {
    const url = constants.API.WEB_DESIGNER.WEB_EVENT.replace(
        ':eventId',
        eventId
    );
    return fetchClient.get(isPublic ? `public/${url}` : url, {
        tokenCheck: false,
    });
};

export const uploadSectionImage = (eventId, webPageId, file) => {
    const config = {
        headers: { 'content-type': 'multipart/form-data' },
    };
    const url = constants.API.WEB_DESIGNER.WEB_PAGE.replace(
        ':eventId',
        eventId
    ).replace(':pageId', webPageId);

    const formData = new FormData();
    formData.append('file', file);

    return fetchClient.post(`${url}/image/`, formData, config);
};

export const deleteSectionImage = (eventId, webPageId, imageId) => {
    const url = constants.API.WEB_DESIGNER.WEB_PAGE.replace(
        ':eventId',
        eventId
    ).replace(':pageId', webPageId);
    return fetchClient.delete(`${url}/image/${imageId}`);
};

export const getWebAgenda = (eventId) => {
    const url = constants.API.WEB_DESIGNER.WEB_AGENDA.replace(
        ':eventId',
        eventId
    );
    return fetchClient.get(`public/${url}`, {
        tokenCheck: false,
    });
};
